import { Component, OnInit, Input, ViewChild, Output, EventEmitter } from '@angular/core';
import { UpdateStatusType, Endpoint, ConnectorType } from '../../models/enums';
import { RestService } from '../../services/rest/rest.service';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../models/base-modal-component';
import { CommonService } from '../../services/common/common.service';
import { SelectionChangedEvent } from 'ag-grid-community';
import { Connector } from '../../models/connector';
import { GridRowSelectionType, GenericColDef, GridContext, SortByFilter } from '../../models/grid-context';
import { GenericTreeNode } from '../generic-tree/generic-tree-node';
import { GenericTreeComponent } from '../generic-tree/generic-tree.component';
import { DrRequestInfo } from '../../models/dr-request-info';
import { GenericGridComponent } from '../generic-grid/generic-grid.component';
import * as _ from 'underscore';

@Component({
    standalone: false,
    selector: 'res-connector-picker',
    templateUrl: './connector-picker.component.html',
    styleUrls: ['./connector-picker.component.scss']
})
export class ConnectorPickerComponent extends BaseModalComponent implements OnInit {
    @Input() checkboxSelectionChangedCallback: (selectedNodes: Array<GenericTreeNode>) => void;
    @ViewChild(GenericTreeComponent, { static: true }) tree: GenericTreeComponent;
    @ViewChild('filesGrid', { static: true }) filesGrid: GenericGridComponent;
    @Input() modalAutomationId: string;
    @Input() treeHeaderText: string;
    @Input() filterConnectors: boolean = false;
    @Output() getPath = new EventEmitter<any>();
    @Output() getConnector = new EventEmitter<any>();
    currentSelectedPath = '';
    path: '';

    // connector grid info
    gridName = 'Connectors';
    gridAutomationId = 'connectors-list-auto-Id';
    updateStatusType = UpdateStatusType.CONNECTOR;
    isGridBusy = false;
    isGridBusyMessage = 'Loading ...';
    isPathsBusy = false;

    // files grid info
    fileGridAutomationId = 'files-grid-auto-Id';
    filesGridUpdateStatusType = UpdateStatusType.CORPUS;
    filesGridRowSelection = GridRowSelectionType.singleCheckboxWithRowClickSelection;
    isFilesBusy = false;
    isFilesBusyMessage = 'Loading ...';
    fileGridRequestInfo: DrRequestInfo;
    selectedFile = { handle: '', name: '' };

    selectedConnector: Connector;
    nodes = new Array<GenericTreeNode>();
    showTreeGridPlaceholder = true;
    selectedNodes: any;
    parentNodeName = '';

    readonly emptyPathParam = { name: '', handle: '', type: 'UNKNOWN', leaf: false };

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Connector Name',
            field: 'name',
            sortBy: SortByFilter.NAME.toString(),
            automationId: '',
            lockVisible: true
        },
        {
            headerName: 'Description',
            field: 'description',
            automationId: ''
        },
        {
            headerName: 'Type',
            field: 'type',
            automationId: '',
            width: 110,
            suppressSizeToFit: true
        },
        {
            headerName: 'Mode',
            field: 'modeDisplay',
            automationId: '',
            width: 95,
            suppressSizeToFit: true
        },
        {
            headerName: 'Server',
            field: 'remoteHost',
            automationId: ''
        },
        {
            headerName: 'Path',
            field: 'remotePath',
            automationId: ''
        }
    );

    filesGridColumnDefs = new Array<GenericColDef>(
        {
            headerName: 'File Name',
            field: 'name',
            automationId: this.fileGridAutomationId,
            lockVisible: true,
            sortBy: SortByFilter.NAME.toString(),
        }
    );

    connectorSelection = GridRowSelectionType.singleCheckboxWithRowClickSelection;

    connectorsGridContext = new GridContext({
        sortAscending: true,
        sortEnabled: false,
        sortBy: SortByFilter.NAME.toString(),
    });

    filesGridContext = new GridContext({
        sortAscending: true,
        sortEnabled: false,
        sortBy: SortByFilter.NAME.toString(),
    });

    requestInfo = {
        endpoint: Endpoint.adminOrgManager_listConnectors,
        responseName: 'connectors',
        data: {
            organizationName: this.commonService?.currentUser?.customerName
        }
    };

    connectorOnSelectionChanged = (event?: SelectionChangedEvent) => {
        this.filesGrid.setData([]);
        this.selectedFile = { handle: '', name: '' };
        if (event?.api) {
            this.selectedConnector = event.api.getSelectedRows()[0];
            const remotePath = this.selectedConnector?.remotePath ? this.selectedConnector.remotePath : '';
            this.parentNodeName = remotePath.substring(remotePath.lastIndexOf('/') + 1);
            if (this.selectedConnector) {
                this.showTreeGridPlaceholder = false;
                this.nodes = [];
                this.loadPaths();
            } else {
                this.showTreeGridPlaceholder = true;
            }
        }
    }

    constructor(
        private modalService: BsModalService,
        private commonService: CommonService,
        private modalRef: BsModalRef,
        private restService: RestService
    ) {
        super(modalService, modalRef);
    }

    ngOnInit() {
        this.columnDefs.forEach((col) => { col.automationId = this.modalAutomationId });
    }

    loadNodeDataCallback = (node: GenericTreeNode) => {
        if (node) {
            this.loadPaths(node);
        }
    }

    filesGridOnSelectionChanged = (event?: SelectionChangedEvent) => {
        if (event && event.api) {
            this.selectedFile = event.api.getSelectedRows()[0];
            if (this.selectedFile) {
                this.currentSelectedPath = this.selectedFile.handle.replace('/' + this.selectedFile.name, '');
            } else {
                this.selectedFile = { name: '', handle: null };
            }
            this.getPath.emit(this.selectedFile.handle);
            this.getConnector.emit({ connector: this.selectedConnector, paths: this.selectedFile.handle });
            this.selectedNodes = [{
                data: {
                    name: this.selectedFile.name,
                    handle: this.currentSelectedPath
                }
            }];
        }
    }

    loadPaths(parent?: GenericTreeNode) {
        let parentPathParam = {};
        if (parent) {
            parent.isLoading = true;
            parentPathParam = parent.data;
        } else {
            this.isPathsBusy = true;
            parentPathParam = this.emptyPathParam;
        }
        this.restService
            .post(Endpoint.connectorManager_exploreConnector, { connectorHandle: this.selectedConnector.handle, parentPath: parentPathParam })
            .then(response => {
                const paths = [];
                if (response?.paths) {
                    response.paths.forEach((element, index) => {
                        if (element.name !== '.' && element.name !== '..') {
                            paths.push(new GenericTreeNode({
                                name: element.name, icon: 'fa-folder', iconExpanded: 'fa-folder-open', iconColor: 'color-yellow-07',
                                isExpandable: !element.leaf, isExpandedOnSelect: !element.leaf,
                                hasExpanderPadding: true, data: element, hasCheckbox: Boolean(this.checkboxSelectionChangedCallback),
                                automationId: element.name + '-' + index
                            }));
                        }
                    });
                }

                if (parent) {
                    this.tree.setNodeData(parent, paths);
                    if (parent.children?.length === 0) {
                        parent.isExpandable = false;
                        parent.iconExpanded = null;
                    }
                    parent.isLoading = false;
                } else {
                    parent = new GenericTreeNode({
                        name: this.parentNodeName, isExpandable: true, isExpandedOnSelect: true, isExpanded: true,
                        hasExpanderPadding: true, data: this.emptyPathParam,
                        automationId: this.parentNodeName
                    });
                    this.nodes = [parent];
                    this.tree.setData(this.nodes);
                    this.tree.setNodeData(parent, paths);
                    this.tree.selectNode(this.tree.data[0]);
                    this.isPathsBusy = false;
                }
            }, () => {
                if (parent) {
                    parent.isLoading = false;
                } else {
                    this.isPathsBusy = false;
                }
            });
    }

    selectedNodeChangedCallback = (node: GenericTreeNode) => {
        this.selectedFile = { handle: '', name: '' };
        if (node) {
            this.selectedFile = { handle: '', name: '' };
            this.getPath.emit(null);
            this.getConnector.emit({ connector: this.selectedConnector, paths: node.data.handle });
            this.refreshFilesGrid(node.data);
        }
    }

    responseCallback = (response: any) => {
        if (response?.connectors) {
            const connectors = new Array<Connector>();
            response.connectors.forEach(element => {
                if (!this.filterConnectors || (this.filterConnectors && element.type !== ConnectorType.GoogleWorkspace)) {
                    const connector = new Connector(element);
                    connectors.push(connector);
                }
            });
            response.connectors = _.sortBy(connectors, [function (c) { return c.name.toLowerCase(); }]);
        }
    }

    filesGridResponseArrayFilter = (value: any) => {
        if (value?.leaf) {
            return new Connector(value);
        }
    }

    refreshFilesGrid(treeSelection: any): void {
        const commonFilters = {
            dat: {
                attribute: 'fileExtension',
                operator: 'EQUALS',
                value: 'dat'
            },
            txt: {
                attribute: 'fileExtension',
                operator: 'EQUALS',
                value: 'txt'
            },
            csv: {
                attribute: 'fileExtension',
                operator: 'EQUALS',
                value: 'csv'
            },
            // lfp is no longer part of load file import in 5.0, but will leave this alone for now
            lfp: {
                attribute: 'fileExtension',
                operator: 'EQUALS',
                value: 'lfp'
            },
            xml: {
                attribute: 'fileExtension',
                operator: 'EQUALS',
                value: 'xml'
            },
            opt: {
                attribute: 'fileExtension',
                operator: 'EQUALS',
                value: 'opt'
            }
        };

        const filters = [commonFilters.dat, commonFilters.lfp, commonFilters.opt, commonFilters.xml, commonFilters.csv, commonFilters.txt];

        this.fileGridRequestInfo = {
            endpoint: Endpoint.connectorManager_exploreConnector,
            responseName: 'paths',
            data: { connectorHandle: this.selectedConnector.handle, filters: filters, parentPath: treeSelection }
        };

        this.filesGrid.requestInfo = this.fileGridRequestInfo;
        this.filesGrid.loadData();
    }
}
